import cn from 'classnames'
import { FC } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { formatDisplayDate } from '../lib/formatDisplayDate'
import { getPrimaryBannerText } from '../lib/getPrimaryBannerText'
import { isReminderSentToday } from '../lib/isReminderSentToday'
import type { AcknowledgmentCommitmentItem, AcknowledgmentPrimaryAction } from '../model/types'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import styles from './AcknowledgmentPrimaryBanner.module.scss'

type AcknowledgmentPrimaryBannerProps = {
  primaryAction: AcknowledgmentPrimaryAction
  isSubmitting: boolean
  onRequestNotify: (item: AcknowledgmentCommitmentItem) => void
}

export const AcknowledgmentPrimaryBanner: FC<AcknowledgmentPrimaryBannerProps> = ({
  primaryAction,
  isSubmitting,
  onRequestNotify,
}) => {
  const t = useSafetyTranslation()

  if (!primaryAction || primaryAction.kind !== 'notify') {
    return null
  }

  const primaryBannerText = getPrimaryBannerText(
    primaryAction,
    getTranslatedText({
      t,
      key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.signerFallback,
    })
  )

  const emailSentDate = primaryAction.item.EmailSentDate
  const sentToday = isReminderSentToday(emailSentDate)
  const canNotify = primaryAction.item.CanResend > 0

  return (
    <div className={cn(styles.primaryBanner, styles.primaryBannerNotify)}>
      <div className={styles.primaryBannerInfo}>
        <span className={styles.primaryBannerIcon}>!</span>
        <span className={styles.primaryBannerText}>
          <Translation
            span
            text={primaryBannerText.key}
            replaceParams={primaryBannerText.replaceParams}
          />
        </span>
      </div>

      <div className={styles.primaryBannerActions}>
        {emailSentDate ? (
          <span className={styles.primaryBannerReminderSent}>
            <Translation
              span
              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.primaryBanner.reminderSent}
              replaceParams={[
                { key: '{date}', value: formatDisplayDate(emailSentDate, 'dateTime') },
              ]}
            />
          </span>
        ) : null}
        {canNotify ? (
          <Button
            className={cn(styles.primaryButton, styles.primaryButtonNotify)}
            size="sm"
            color="warning"
            disabled={isSubmitting || sentToday}
            onClick={() => onRequestNotify(primaryAction.item)}
          >
            <Translation text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.notify} span />
          </Button>
        ) : null}
      </div>
    </div>
  )
}
